import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { Save, Shield, Share2, Copy, RefreshCw, UserPlus, Download, Smartphone } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { settings, fetchSettings } = useAuth();
  const [form, setForm] = useState({ app_name: "", app_logo_url: "" });
  const [saving, setSaving] = useState(false);
  const [shareToken, setShareToken] = useState("");
  const [inviteToken, setInviteToken] = useState("");
  const [rotating, setRotating] = useState(false);
  const [rotatingInvite, setRotatingInvite] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setForm({ app_name: settings.app_name || "", app_logo_url: settings.app_logo_url || "" });
  }, [settings]);

  useEffect(() => {
    (async () => {
      try {
        const [s, i] = await Promise.all([
          api.get("/share/token"),
          api.get("/invite/token"),
        ]);
        setShareToken(s.data.token);
        setInviteToken(i.data.token);
      } catch (e) {}
    })();
  }, []);

  const shareUrl = shareToken
    ? `${window.location.origin}/public/${shareToken}`
    : "";
  const inviteUrl = inviteToken
    ? `${window.location.origin}/invite/${inviteToken}`
    : "";

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copié`);
    } catch (e) {
      toast.error("Copie impossible");
    }
  };

  const rotate = async () => {
    if (!confirm("Générer un nouveau lien public ? L'ancien deviendra invalide.")) return;
    setRotating(true);
    try {
      const { data } = await api.post("/share/rotate");
      setShareToken(data.token);
      toast.success("Nouveau lien généré");
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setRotating(false);
    }
  };

  const rotateInvite = async () => {
    if (!confirm("Générer un nouveau lien d'invitation ? L'ancien deviendra invalide.")) return;
    setRotatingInvite(true);
    try {
      const { data } = await api.post("/invite/rotate");
      setInviteToken(data.token);
      toast.success("Nouveau lien d'invitation");
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setRotatingInvite(false);
    }
  };

  const exportJson = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/export`, { credentials: "include" });
      if (!res.ok) throw new Error("bad");
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `ass-les-regionnaires-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Sauvegarde téléchargée");
    } catch (e) {
      toast.error("Erreur d'export");
    } finally {
      setExporting(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      await fetchSettings();
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error("Logo trop volumineux (max 1 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, app_logo_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-500 mb-2">
          Configuration
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
          Paramètres
        </h1>
      </div>

      <form onSubmit={save} className="rounded-2xl bg-slate-900 border border-white/10 p-6 space-y-6">
        <div>
          <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
            Nom de l'application
          </label>
          <input
            data-testid="settings-app-name"
            type="text"
            value={form.app_name}
            onChange={(e) => setForm({ ...form, app_name: e.target.value })}
            className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none text-white"
            placeholder="AS les Régionnaires FC"
          />
        </div>

        <div>
          <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
            Logo global
          </label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-800/50 flex items-center justify-center">
              {form.app_logo_url ? (
                <img
                  src={form.app_logo_url}
                  alt="Logo preview"
                  className="w-full h-full object-cover"
                  data-testid="logo-preview"
                />
              ) : (
                <Shield className="w-10 h-10 text-slate-500" />
              )}
            </div>
            <div className="flex-1 space-y-2">
              <input
                data-testid="settings-logo-file"
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="block w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#E5B85C] file:text-[#0B0F17] file:font-bold file:uppercase file:text-xs file:cursor-pointer"
              />
              <input
                data-testid="settings-logo-url"
                type="url"
                value={form.app_logo_url}
                onChange={(e) => setForm({ ...form, app_logo_url: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-sm text-white"
                placeholder="ou URL du logo (https://...)"
              />
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Upload une image (max 1 Mo) ou colle une URL. Le logo apparaîtra dans le header et sur les feuilles de match.
          </p>
        </div>

        <button
          data-testid="settings-save-button"
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? "Enregistrement..." : "Enregistrer"}
        </button>
      </form>

      <div className="rounded-2xl bg-slate-900 border border-white/10 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-[#E5B85C]" />
          <div className="font-display font-bold text-lg uppercase">Installer l'app</div>
        </div>
        <p className="text-sm text-slate-400">
          Un lien pour installer l'app sur Android et iPhone (aucun store nécessaire). Envoie-le à tous les membres du club.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="install-url-input"
            readOnly
            value={`${window.location.origin}/install`}
            className="flex-1 px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white text-sm font-mono-stat"
          />
          <button
            data-testid="copy-install-button"
            onClick={() => copyText(`${window.location.origin}/install`, "Lien d'installation")}
            className="inline-flex items-center gap-2 px-4 py-3 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg text-sm"
          >
            <Copy className="w-4 h-4" /> Copier
          </button>
          <a
            data-testid="open-install-button"
            href="/install"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold uppercase tracking-wide rounded-lg text-sm"
          >
            <Smartphone className="w-4 h-4" /> Aperçu
          </a>
        </div>
        <div className="flex items-center gap-3 mt-1">
          <img
            src={`${API}/install-qr.png`}
            alt="QR d'installation"
            className="w-24 h-24 rounded-lg bg-slate-800 p-1.5"
            data-testid="install-qr-preview"
          />
          <p className="text-xs text-slate-500 leading-relaxed max-w-xs">
            Scanne ce QR avec un téléphone pour ouvrir la page d'installation.
            Idéal pour la partager en réunion ou sur un flyer.
          </p>
        </div>
      </div>

      <div className="rounded-2xl bg-slate-900 border border-white/10 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Share2 className="w-5 h-5 text-[#E5B85C]" />
          <div className="font-display font-bold text-lg uppercase">Partage public</div>
        </div>
        <p className="text-sm text-slate-400">
          Un lien lecture-seule pour montrer classement + résultats à la famille et aux fans, sans compte.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="share-url-input"
            readOnly
            value={shareUrl}
            className="flex-1 px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white text-sm font-mono-stat"
          />
          <button
            data-testid="copy-share-button"
            onClick={() => copyText(shareUrl, "Lien public")}
            disabled={!shareUrl}
            className="inline-flex items-center gap-2 px-4 py-3 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg text-sm disabled:opacity-50"
          >
            <Copy className="w-4 h-4" /> Copier
          </button>
          <button
            data-testid="rotate-share-button"
            onClick={rotate}
            disabled={rotating}
            className="inline-flex items-center gap-2 px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold uppercase tracking-wide rounded-lg text-sm disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" />
            Régénérer
          </button>
        </div>
      </div>

      <div className="rounded-2xl bg-slate-900 border border-white/10 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[#E5B85C]" />
          <div className="font-display font-bold text-lg uppercase">Inviter des joueurs</div>
        </div>
        <p className="text-sm text-slate-400">
          Partage ce lien à tes coéquipiers : ils créent leur compte joueur en 30 secondes, sans que tu bosses.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="invite-url-input"
            readOnly
            value={inviteUrl}
            className="flex-1 px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white text-sm font-mono-stat"
          />
          <button
            data-testid="copy-invite-button"
            onClick={() => copyText(inviteUrl, "Lien d'invitation")}
            disabled={!inviteUrl}
            className="inline-flex items-center gap-2 px-4 py-3 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg text-sm disabled:opacity-50"
          >
            <Copy className="w-4 h-4" /> Copier
          </button>
          <button
            data-testid="rotate-invite-button"
            onClick={rotateInvite}
            disabled={rotatingInvite}
            className="inline-flex items-center gap-2 px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold uppercase tracking-wide rounded-lg text-sm disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" />
            Régénérer
          </button>
        </div>
      </div>

      <div className="rounded-2xl bg-slate-900 border border-white/10 p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Download className="w-5 h-5 text-[#E5B85C]" />
          <div className="font-display font-bold text-lg uppercase">Sauvegarde saison</div>
        </div>
        <p className="text-sm text-slate-400">
          Télécharge un JSON complet (joueurs, matchs, stats, dispos) — copie locale au cas où.
        </p>
        <button
          data-testid="export-json-button"
          onClick={exportJson}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-3 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg text-sm disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {exporting ? "Export..." : "Télécharger JSON"}
        </button>
      </div>
    </div>
  );
}
